import {
    formatDate,
    isValid,
    parse,
    setHours,
    setMinutes,
    setSeconds,
} from '../../helpers/date'
import { DateFormat } from '../../types/dateFormatTypes'
import {
    AddChangeLogEntryFormData,
    AddChangeLogEntryRequest,
    ChangeLogEntry,
    ChangeLogEntryResource,
    EditChangeLogEntryFormData,
    EditChangeLogEntryRequest,
} from './ChangeLog'

export const transformToChangeLogEntry = (resource: ChangeLogEntryResource): ChangeLogEntry => ({
    ...resource,
})

export const transformDateAndTimeToChangeLogEntryDateString = (date: Date, time: string): string => {
    const [hours, minutes] = time.split(':').map(Number)
    const combinedDate = setSeconds(setMinutes(setHours(date, hours), minutes), 0)

    return formatDate(combinedDate, DateFormat.apiDateTime)
}

/**
 * Falls back to `now` when a date string can't be parsed — older changelog entries were written with
 * inconsistent, hand-typed date formats (Dutch month names, no fixed pattern) that predate this form.
 */
export const parseChangeLogEntryDateString = (dateString: string): Date => {
    const parsedDate = parse(dateString, DateFormat.apiDateTime, new Date())

    return isValid(parsedDate) ? parsedDate : new Date()
}

export const transformChangesStringToChangesArray = (changes: string): string[] => changes
    .split('\n')
    .map(change => change.trim())
    .filter(change => !! change)

export const transformChangesArrayToChangesString = (changes: string[]): string => changes.join('\n')

export const transformAddChangeLogEntryFormDataToAddApiParams = (
    formData: AddChangeLogEntryFormData,
): AddChangeLogEntryRequest => ({
    version: formData.version,
    date: formData.date,
    intro: formData.intro || undefined,
    changes: transformChangesStringToChangesArray(formData.changes),
    popupMessage: formData.popupMessage ? { text: formData.popupMessage } : undefined,
})

export const transformEditChangeLogEntryFormDataToEditApiParams = (
    formData: EditChangeLogEntryFormData,
): EditChangeLogEntryRequest => ({
    id: formData.id,
    version: formData.version,
    date: formData.date,
    intro: formData.intro || undefined,
    changes: transformChangesStringToChangesArray(formData.changes),
    popupMessage: formData.popupMessage ? { text: formData.popupMessage } : undefined,
})

export const transformChangeLogEntryToEditChangeLogEntryFormData = (
    changeLogEntry: ChangeLogEntry,
): EditChangeLogEntryFormData => ({
    id: changeLogEntry.id,
    version: changeLogEntry.version,
    date: changeLogEntry.date,
    intro: changeLogEntry.intro || '',
    changes: transformChangesArrayToChangesString(changeLogEntry.changes),
    popupMessage: changeLogEntry.popupMessage?.text?.split('\\n')?.join('\\r\\n') || '',
})
